import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import PartnerHeader from "./components/PartnerHeader";
import PartnerTabNavigation from "./components/PartnerTabNavigation";

import SummaryTab from "./components/SummaryTab";
import JobsTab from "./components/JobsTab";
import PaymentsTab from "./components/PaymentsTab";
import WithdrawalRequestsTab from "./components/WithdrawalRequestsTab";
import MakePaymentTab from "./components/MakePaymentTab";

// reuse booking adjustment tab
import AdjustmentsTab from "../seeb/booking/components/AdjustmentsTab";

// verification page
import PartnerVerification from "./PartnerDetailVerification";

type PartnerTabType =
  | "summary"
  | "jobs"
  | "payments"
  | "adjustments"
  | "withdrawal_requests"
  | "make_payment"
  | "verification";

type WalletTransaction = {
  id: number;
  date: string;
  purpose: string;
  reference?: string;
  credit: number;
  debit: number;
  penalty: number;
  status: "completed" | "pending";
};

const PartnerDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ READ WHAT YOU SENT
  const partner = location.state?.partner;

  const [activeTab, setActiveTab] = useState<PartnerTabType>("summary");
  const [walletTransactions, setWalletTransactions] =
    useState<WalletTransaction[]>([]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/list-partner");
  };

  const handleAddToWallet = (txn: Omit<WalletTransaction, "id">) => {
    setWalletTransactions((prev) => {
      const nextId =
        prev.length > 0 ? Math.max(...prev.map((t) => t.id)) + 1 : 1;
      return [{ ...txn, id: nextId }, ...prev];
    });
  };

  const handleWithdrawalApproved = (txn: Omit<WalletTransaction, "id">) => {
    setWalletTransactions((prev) => {
      const nextId =
        prev.length > 0 ? Math.max(...prev.map((t) => t.id)) + 1 : 1;
      return [{ ...txn, id: nextId }, ...prev];
    });
  };

  // hard guard — no silent broken page
  if (!partner) {
    navigate("/list-partner");
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="mt-6 p-6 bg-white rounded-lg shadow w-full">
        {/* HEADER */}
        <PartnerHeader partner={partner} onBack={handleBack} />

        {/* TAB NAVIGATION */}
        <PartnerTabNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        {/* TAB CONTENT */}
        <div className="mt-6">
          {activeTab === "summary" && (
            <SummaryTab partner={partner} />
          )}

          {activeTab === "jobs" && (
            <JobsTab partnerId={partner.id} />
          )}

          {activeTab === "payments" && (
            <PaymentsTab
              partner={partner}
              transactions={walletTransactions}
              onAddToWallet={handleAddToWallet}
            />
          )}

          {activeTab === "adjustments" && <AdjustmentsTab />}

          {activeTab === "withdrawal_requests" && (
            <WithdrawalRequestsTab
              onApprove={handleWithdrawalApproved}
            />
          )}

          {activeTab === "make_payment" && (
            <MakePaymentTab partner={partner} />
          )}

          {activeTab === "verification" && (
            <PartnerVerification partner={partner} />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PartnerDetails;
